import { type ReactNode, useState } from 'react';
import { Menu, type MenuProps, ScrollArea, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';

type SearchableMenuOption = { id: string; label: string };

const DROPDOWN_MAX_HEIGHT = 240;
const SEARCH_MIN_OPTIONS = 6;

type Props = Pick<MenuProps, 'position' | 'width' | 'withinPortal'> & {
  target: ReactNode;
  options: SearchableMenuOption[];
  onSelectOption: (id: string) => void;
  optionNoun: string;
};

export function SearchableMenu({
  target,
  options,
  onSelectOption,
  optionNoun,
  ...menuProps
}: Props) {
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();
  const filteredOptions = query
    ? options.filter(option => option.label.toLowerCase().includes(query))
    : options;
  const withSearch = options.length >= SEARCH_MIN_OPTIONS;

  return (
    <Menu
      {...menuProps}
      // Mantine autofocuses its own placeholder element first, which would leave the
      // search box needing a second click before you can type in it.
      withInitialFocusPlaceholder={!withSearch}
      // Resetting on `onClose` would flash the unfiltered list back in while the
      // dropdown is still fading out.
      onExitTransitionEnd={() => setSearch('')}
      styles={{ itemLabel: { minWidth: 0, overflowWrap: 'anywhere' } }}
    >
      <Menu.Target>{target}</Menu.Target>
      <Menu.Dropdown>
        {withSearch && (
          <TextInput
            data-autofocus
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            placeholder={`Search ${optionNoun}s`}
            aria-label={`Search ${optionNoun}s`}
            leftSection={<IconSearch size={14} />}
            size="xs"
            mb="xxs"
          />
        )}
        {options.length === 0 && <Menu.Item disabled>No options available</Menu.Item>}
        {options.length > 0 && filteredOptions.length === 0 && (
          <Menu.Item disabled>No matching {optionNoun}s</Menu.Item>
        )}
        <ScrollArea.Autosize mah={DROPDOWN_MAX_HEIGHT} type="auto">
          {filteredOptions.map(option => (
            <Menu.Item key={option.id} onClick={() => onSelectOption(option.id)}>
              {option.label}
            </Menu.Item>
          ))}
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  );
}
